import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { TERMS_NOT_ACCEPTED } from '@/features/welcome/api/client';
import { useAuth } from '@/hooks/use-auth';
import { ApiError, apiErrorCode } from '@/lib/http';
import { storefrontApi } from '../api/client';
import { storefrontKeys } from '../api/keys';
import type { CartDto, CheckoutResponse } from '../api/types';

/** Teto de unidades por linha no backend (`MAX_LINE_QUANTITY`). */
export const MAX_LINE_QUANTITY = 100;

/**
 * O carrinho do buyer, revalidado a cada leitura.
 *
 * **Carrinho vazio é 200 com lista vazia, não 404** — o `GET` não cria sessão.
 * "Ainda não adicionei nada" é estado normal da vitrine.
 */
export function useCart({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<CartDto, ApiError>({
    queryKey: storefrontKeys.cart(),
    queryFn: () => storefrontApi.cart(),
    enabled,
    // Curto de propósito: as linhas carregam veredito do instante (estoque,
    // marca revogada, preço) e um carrinho velho promete o que não entrega.
    staleTime: 15_000,
  });
}

/**
 * "Alguma destas peças já está na sacola?" — sem assinar a sacola inteira.
 *
 * A listagem monta um destes por CARD. Com `useCart()` cada card assinava o
 * carrinho todo, e uma adição re-renderizava a grade INTEIRA: o `setQueryData`
 * da escrita notifica todo assinante daquela chave, e trinta cards
 * recalculavam, reflowavam e repintavam por causa de uma linha que mudou.
 *
 * `select` corta isso na raiz. Ele roda sobre o dado novo, mas o que sai é um
 * BOOLEANO: o TanStack compara o resultado e só notifica quem mudou de valor.
 * Adicionar a primeira peça re-renderiza o card dela — e mais nenhum.
 *
 * Continua sendo a mesma query (mesma `queryKey`, mesma `queryFn`), então não
 * há request a mais: o cache é um só, e isto é uma leitura recortada dele.
 */
export function useNaSacola(productIds: readonly string[]): boolean {
  const alvos = new Set(productIds);

  const { data } = useQuery<CartDto, ApiError, boolean>({
    queryKey: storefrontKeys.cart(),
    queryFn: () => storefrontApi.cart(),
    staleTime: 15_000,
    select: (cart) => cart.lines.some((line) => alvos.has(line.product_id)),
  });

  return data ?? false;
}

/** Toda escrita devolve o carrinho inteiro — o cache recebe a resposta. */
function useCartWrite<TVars>(
  mutationFn: (vars: TVars) => Promise<CartDto>,
  successMessage?: (vars: TVars) => string
) {
  const queryClient = useQueryClient();
  return useMutation<CartDto, ApiError, TVars>({
    mutationFn,
    onSuccess: (cart, vars) => {
      queryClient.setQueryData(storefrontKeys.cart(), cart);
      // A hidratação da página que ficou atrás muda junto: `max_addable` é
      // `min(estoque, MAX_ADDABLE_UI)`, e o estoque se move enquanto se
      // navega — inclusive por escrita de outra aba.
      queryClient.invalidateQueries({ queryKey: storefrontKeys.availabilityAll() });
      if (successMessage) toast.success(successMessage(vars));
    },
    onError: (error) => toast.error(cartErrorMessage(error)),
  });
}

export function useAddToCart() {
  return useCartWrite<{ productId: string; quantity: number }>(
    ({ productId, quantity }) => storefrontApi.addToCart(productId, quantity),
    () => 'Peça adicionada ao carrinho'
  );
}

export function useSetCartQuantity() {
  return useCartWrite<{ productId: string; quantity: number }>(
    ({ productId, quantity }) =>
      storefrontApi.setCartQuantity(productId, quantity)
  );
}

export function useRemoveCartLine() {
  return useCartWrite<{ productId: string }>(
    ({ productId }) => storefrontApi.removeCartLine(productId),
    () => 'Peça removida do carrinho'
  );
}

/**
 * Genéricos explícitos porque o `onError` de quem chama precisa do `status`: o
 * `TError` padrão do TanStack é `Error`, e sem isto o call site não distingue
 * 409 (loja fechada / estoque) de 500.
 */
export function useCheckout() {
  const queryClient = useQueryClient();
  const { refreshSession } = useAuth();
  return useMutation<CheckoutResponse, ApiError, void>({
    mutationFn: () => storefrontApi.checkout(),
    onSuccess: () => {
      // A sessão virou SUBMITTED e saiu do índice parcial: o próximo
      // "adicionar" cria outra. Reler agora devolve o carrinho vazio.
      queryClient.invalidateQueries({ queryKey: storefrontKeys.cart() });
      // A confirmação linka cada pedido criado (`orders`, um ou dois): a lista
      // de "Meus pedidos" em cache ainda não os tem.
      queryClient.invalidateQueries({ queryKey: storefrontKeys.ordersAll() });
    },
    onError: (error) => {
      toast.error(cartErrorMessage(error));
      // Termos não aceitos (vencidos com a aba aberta): reler a sessão traz
      // `termsPending` e o gate da vitrine reabre o wizard.
      if (apiErrorCode(error) === TERMS_NOT_ACCEPTED) void refreshSession();
    },
  });
}

/**
 * Mensagem de erro do carrinho.
 *
 * 409 é o caso que merece texto próprio: "loja fechada" (sem campanha ativa) e
 * "não sobrou estoque" chegam pelo mesmo status, e o backend já manda o texto
 * — repetir a regra aqui é a duplicação que o `missing_for_minimum` existe
 * para evitar.
 */
export function cartErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409 || error.status === 400) return error.message;
    if (error.status === 404) {
      return 'Seu carrinho não está mais aberto. Atualize a página.';
    }
    if (error.status === 422) {
      return `Quantidade fora do limite permitido (máximo ${MAX_LINE_QUANTITY} por peça).`;
    }
  }
  return 'Não foi possível atualizar o carrinho. Tente de novo.';
}
